/**
 * Icons: a small, hand-held set of stroke glyphs, inline.
 *
 * Inline rather than a dependency for three reasons. The icon libraries the ecosystem reaches for
 * ship thousands of glyphs and a tree-shaking story that fails quietly the moment an icon name is
 * computed rather than literal; the ceremony's budget (ux.md §1: ≤ 120 KB gzipped before pdf.js)
 * is not the place to find that out. A glyph is `currentColor`, so it follows the semantic text
 * tokens and dark mode without knowing either exists. And every icon here is decorative —
 * `aria-hidden` — because an icon without a visible label is a puzzle, not an affordance: the
 * label carries the meaning, the glyph is there for scanning speed.
 *
 * 24-unit grid, 1.75 stroke, round joins — the Lucide conventions, so a glyph added later from
 * that set sits beside these without looking borrowed.
 */
import type { ReactNode, SVGProps } from 'react'

const GLYPHS = {
  home: (
    <>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </>
  ),
  'file-text': (
    <>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </>
  ),
  template: (
    <>
      <rect width="18" height="7" x="3" y="3" rx="1" />
      <rect width="9" height="7" x="3" y="14" rx="1" />
      <rect width="5" height="7" x="16" y="14" rx="1" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v18h18" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </>
  ),
  sliders: (
    <>
      <path d="M21 4h-7" />
      <path d="M10 4H3" />
      <path d="M21 12h-9" />
      <path d="M8 12H3" />
      <path d="M21 20h-5" />
      <path d="M12 20H3" />
      <path d="M14 2v4" />
      <path d="M8 10v4" />
      <path d="M16 18v4" />
    </>
  ),
  shield: (
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  ),
  code: (
    <>
      <path d="m16 18 6-6-6-6" />
      <path d="m8 6-6 6 6 6" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  send: (
    <>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </>
  ),
  'log-out': (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  // Two offset rounded rectangles — the Lucide `copy` glyph, on the same 24-unit grid. The set had
  // no copy mark at all, because until now every copy affordance in the console was a Button with
  // the word on it.
  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
  upload: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m17 8-5-5-5 5" />
      <path d="M12 3v12" />
    </>
  ),
  plus: (
    <>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </>
  ),
  'chevron-right': <path d="m9 18 6-6-6-6" />,
  'arrow-right': (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  /*
    The mirror of `arrow-right`, and the reason it exists is that there was no left arrow at all.
    Every screen that needed to point backwards therefore pointed with a WORD — "Back to
    documents", "Set Up Envelope", "Documents" — while the way forward on the same screen was an
    arrow, so one flow drew its two directions in two different alphabets (ADR 0141).

    Drawn as the reflection of the right arrow rather than as a chevron. This set's chevrons are
    `chevron-right`, `chevron-down` and `chevron-up` — the fold controls use the last two — and
    reusing one for a step hop would make "expand this section" and "go back a step" the same
    glyph. There is deliberately still NO `chevron-left`: the one place that wants a left-pointing
    chevron rotates the right one (`AdminShell.tsx`, `chevron-right` with `rotate-180`), and a
    second glyph that draws the same shape is a second thing to keep consistent.
  */
  'arrow-left': (
    <>
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </>
  ),
  'chevrons-up-down': (
    <>
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </>
  ),
  'circle-alert': (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </>
  ),
  'circle-check': (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  inbox: (
    <>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),
  pen: (
    <>
      <path d="M12 20h9" />
      <path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </>
  ),
  activity: (
    <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </>
  ),
  lock: (
    <>
      <rect width="18" height="11" x="3" y="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  'external-link': (
    <>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </>
  ),
  menu: (
    <>
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </>
  ),
  x: (
    <>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </>
  ),
  building: (
    <>
      <rect width="16" height="20" x="4" y="2" rx="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M12 6h.01" />
      <path d="M12 10h.01" />
      <path d="M12 14h.01" />
      <path d="M16 10h.01" />
      <path d="M16 14h.01" />
      <path d="M8 10h.01" />
      <path d="M8 14h.01" />
    </>
  ),
  /* The eight below arrived with the Reports redesign: a date-range control, a CSV export, the
     outcome chips (a declined envelope, a rate) and the period-over-period arrows on its tiles. */
  calendar: (
    <>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M3 10h18" />
    </>
  ),
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  'chevron-up': <path d="m18 15-6-6-6 6" />,
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
      <path d="M12 15V3" />
    </>
  ),
  'circle-x': (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </>
  ),
  percent: (
    <>
      <path d="M19 5 5 19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </>
  ),
  'trending-up': (
    <>
      <path d="M22 7 13.5 15.5 8.5 10.5 2 18" />
      <path d="M16 7h6v6" />
    </>
  ),
  'trending-down': (
    <>
      <path d="M22 17 13.5 8.5 8.5 13.5 2 6" />
      <path d="M16 17h6v-6" />
    </>
  ),
  minus: <path d="M5 12h14" />,
  // The overflow trigger every row menu hangs off. Three dots rather than a caret, because the
  // caret already means "this opens downward" on the org and workspace switchers and two meanings
  // for one glyph is how a control stops being learnable.
  'more-horizontal': (
    <>
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
    </>
  ),
  // Deletion, wherever it is real. The console's destructive acts are mostly soft — a workspace is
  // re-homed, a member is suspended — and this glyph is deliberately reserved for the ones that are
  // not, so that a bin never appears next to something that will still be there afterwards.
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M10 11v6M14 11v6" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </>
  ),
  // A team. `users` is a group of people and this is the container they sit in; the workspace
  // switcher needs to be distinguishable from the member list at a glance.
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </>
  ),

  /* The field palette's glyphs (the founder's Add Fields screen, 2026-09-13). One per palette
     item, because a two-column grid of word-only buttons is read letter by letter while a grid of
     icon-and-word cards is scanned — which is the whole difference between the palette a sender
     hunts through and the one they reach into. Every one is decorative: the label beside it
     carries the meaning, exactly as the header of this file says. */
  signature: (
    <>
      <path d="M2 16.2c2.6 0 3.8-2.4 4.8-6.4C7.9 5.3 8.6 2.5 10.4 2.5c1.2 0 1.7 1.2 1.1 3.3-.9 3.3-3 7.4-3 9.8 0 1.3.7 2.1 1.8 2.1 1.6 0 2.6-2 4.1-2 1.1 0 1.5 1.2 2.8 1.2 1 0 1.8-.6 2.8-1.8" />
      <path d="M3 21.2h18" />
    </>
  ),
  /* "AZ" — SignNow's own mark for an initial, and the one thing a sender never confuses with a
     full signature. Letterforms rather than a monogram circle, which reads as an avatar. */
  initials: (
    <>
      <path d="M3 17 6.5 7 10 17" />
      <path d="M4.2 14h4.6" />
      <path d="M13 7h8l-8 10h8" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </>
  ),
  'id-card': (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <circle cx="8" cy="11.5" r="2" />
      <path d="M5.5 16.5a3 3 0 0 1 5 0" />
      <path d="M14 10h5" />
      <path d="M14 14h5" />
    </>
  ),
  type: (
    <>
      <path d="M4 7V5h16v2" />
      <path d="M12 5v14" />
      <path d="M9 19h6" />
    </>
  ),
  hash: (
    <>
      <path d="M5 9h14" />
      <path d="M5 15h14" />
      <path d="M11 4 9 20" />
      <path d="M15 4l-2 16" />
    </>
  ),
  'square-check': (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="m8 12 3 3 5-6" />
    </>
  ),
  'square-chevron-down': (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m8.5 10.5 3.5 3.5 3.5-3.5" />
    </>
  ),
  'circle-dot': (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  stamp: (
    <>
      <path d="M5 22h14" />
      <path d="M19.27 13.73A2.5 2.5 0 0 0 17.5 13h-11A2.5 2.5 0 0 0 4 15.5V19h16v-3.5c0-.66-.26-1.3-.73-1.77z" />
      <path d="M14 13V8.5C14 7 15 7 15 5a3 3 0 0 0-6 0c0 2 1 2 1 3.5V13" />
    </>
  ),
  note: (
    <>
      <path d="M4 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v9l-6 6H5a1 1 0 0 1-1-1z" />
      <path d="M20 14h-5a1 1 0 0 0-1 1v5" />
    </>
  ),
  sigma: <path d="M18 7V4H6l6 8-6 8h12v-3" />,
  paperclip: (
    <path d="m16.5 7.5-7.8 7.8a1.75 1.75 0 0 0 2.5 2.5l8.3-8.3a3.5 3.5 0 0 0-5-5l-8.3 8.3a5.25 5.25 0 0 0 7.4 7.4l7.4-7.4" />
  ),
} satisfies Record<string, ReactNode>

export type IconName = keyof typeof GLYPHS

export const ICON_NAMES = Object.keys(GLYPHS) as readonly IconName[]

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'name' | 'children'> & {
  readonly name: IconName
  /** Rendered size in CSS pixels. 16 sits on a 32 px row; 20 on a 36 px control. */
  readonly size?: 12 | 14 | 16 | 18 | 20 | 24 | 32
}

/** A decorative glyph. It never carries meaning on its own; the visible label beside it does. */
export function Icon({ name, size = 16, className = '', ...rest }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      {...rest}
    >
      {GLYPHS[name]}
    </svg>
  )
}
